import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {NavLink} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faCalendarDay,
  faEdit,
  faEye,
  faEyeSlash,
  faFileExport,
  faFileMedical,
  faKeyboard,
  faSearch,
  faUpload,
  faUpRightFromSquare
} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";

// Contexts
import {AuthContext} from "../../contexts/auth.js";
import {SettingsContext} from "../../contexts/settings.js";
import {useToast} from "../../contexts/toast.js";

// Hooks
import useApi from "../../hooks/useApi.js";
import useMountedState from "../../hooks/useMountedState.js";
import usePrevious from "../../hooks/usePrevious.js";

// Utils
import {hexToRgba, prettyDateInUserTimezone} from "../../utils/helpers.js";
import {isMobileSize} from "../../utils/responsive.js";
import {megabytesToBytes, uploadFilesInBatch, uploadErrorMessage} from "../../utils/fileUpload.js";
import {resolveCustomTableSort} from "./customTableSort.js";

// Components
import ToggleSwitch from "../../components/ToggleSwitch.js";
import BaseTable from "../../components/BaseTable.js";
import Dropdown from "../../components/Dropdown.js";
import Pagination from "../../components/Pagination.js";
import ModalRowAdd from "./ModalRowAdd.js";
import ModalRowPreview from "./ModalRowPreview.js";
import ModalRowDelete from "./ModalRowArchive.js";
import ModalBulkTagUpdate from "./ModalBulkTagUpdate.js";
import ModalExport from "../../components/ModalExport.js";
import ModalFailedUpload from "../facility/ModalFailedUpload.js";
import {AttachmentCell, AttachmentColumnHeader} from "./AttachmentCell.js";

// Styles
import {z} from "../../style/components/mixins.js";
import {bp} from "../../style/components/breakpoints.js";
import {voice} from "../../style/components/typography.js";
import {pad, transition} from "../../style/components/variables.js";
import {
  Inline,
  Heading,
  TableWrapper,
  TableMenu,
  TableFooter,
  Search,
  SearchWrapper,
  SearchIcon,
  Button,
  Arrow,
  Text,
  TableEditMenu,
  TableEditMenuButton,
  Abbr,
  Input,
  Warning
} from "../../style/components/general.js";

const CustomTable = ({
  index,
  table,
  active,
  setActive,
  handleEditTable,
  handleViewAll,
  inGlobalTables = false,
  onOpen,
  triggerOpen,
  handledOpen
}) => {
  const {
    id: tableId,
    name,
    label,
    columns,
    fileOnly,
    tags,
    facility: {name: facilityName, slug: facilitySlug, id: facilityId, isDeleted, type}
  } = table;

  const isMounted = useMountedState();

  const {addToast} = useToast();

  const {currentUser, roleCanAccessPage, roleCanAccessResource, atLeast} = useContext(AuthContext);

  const {settings} = useContext(SettingsContext);
  const {timezone} = settings;

  const maxFileSize = useMemo(() => {
    let {maxFileSize: current} = settings;
    if (!current) current = 5;
    else {
      current = parseInt(current, 10);
      if (!current || Number.isNaN(current)) current = 5;
      else if (current > 100) current = 100;
    }
    return current;
  }, [settings]);

  // Custom Table
  const [open, setOpen] = useState(false);
  const [tableData, setTableData] = useState(null);
  const [headings, setHeadings] = useState({});

  // Base Table
  const [items, setItems] = useState([]);
  const [searchType, setSearchType] = useState("text");
  const [showOptions, setShowOptions] = useState(false);
  const [targetRow, setTargetRow] = useState(null);
  const [showModalAdd, setShowModalAdd] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [showModalPreview, setShowModalPreview] = useState(null);
  const [showModalBulkTags, setShowModalBulkTags] = useState(false);
  const [showModalExport, setShowModalExport] = useState(false);
  const [showModalFailedUpload, setShowModalFailedUpload] = useState(false);
  const [query, setQuery] = useState(null);
  const [failedUploads, setFailedUploads] = useState(false);
  const [validUploads, setValidUploads] = useState(false);

  const initialSort = resolveCustomTableSort(table);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageTotal, setPageTotal] = useState(1);
  const [orderBy, setOrderBy] = useState(initialSort.orderBy);
  const [groupBy, setGroupBy] = useState(initialSort.groupBy);
  const [limits, setLimits] = useState([]);
  const [limit, setLimit] = useState(10);

  // Drop Files
  const [showDropzone, setShowDropzone] = useState(false);

  const dropzone = useRef();

  const showDeleted = useRef(false);

  // Previous Hooks
  const prevOrderBy = usePrevious(orderBy);
  const prevGroupBy = usePrevious(groupBy);
  const prevLimit = usePrevious(limit);

  const {api, loading} = useApi("custom-table-rows", {suppress: {longRequest: false}});

  const formatTableData = useCallback(
    payload =>
      payload.map(item => {
        const newItem = {};

        const isSuper = !currentUser.role?.id && atLeast("super");
        const canAccess =
          isSuper ||
          !item.roles?.length ||
          item.roles.some(role => currentUser.role?.id === role.id);

        Object.keys(headings).forEach(heading => {
          if (heading === "preview" && roleCanAccessResource("custom_table_row", "view")) {
            if (!canAccess)
              newItem[heading] = (
                <NotAllowed
                  onClick={() => {
                    addToast("You do not have permission to view this row", "error");
                  }}>
                  <Abbr title="You do not have permission to view this row">View</Abbr>
                </NotAllowed>
              );
            else
              newItem[heading] = (
                <Button
                  onClick={() => {
                    setTargetRow(item);
                    setShowModalPreview(true);
                  }}>
                  View
                </Button>
              );
          } else if (heading === "fileCount") {
            newItem[heading] = (
              <AttachmentCell
                count={item.files?.length || 0}
                onOpen={() => {
                  if (!canAccess || !roleCanAccessResource("custom_table_row", "view")) {
                    addToast("You do not have permission to view this row", "error");
                    return;
                  }
                  setTargetRow(item);
                  setShowModalPreview(true);
                }}
              />
            );
          } else if (heading === "createdAt" || heading === "updatedAt")
            newItem[heading] = prettyDateInUserTimezone(item[heading], timezone);
          else if (heading === "date")
            newItem[heading] = prettyDateInUserTimezone(item[heading], timezone, "MMM D YYYY");
          else if (heading === "expiration")
            if (item[heading]) {
              const parsed = prettyDateInUserTimezone(item[heading], timezone, "MMM D, YYYY");
              newItem[heading] = (
                <Expiration expired={dayjs.utc().tz(timezone).isAfter(dayjs(parsed), "day")}>
                  {parsed}
                </Expiration>
              );
            } else newItem[heading] = "";
          else newItem[heading] = item[heading];
          newItem.message = item.message;
          newItem.files = item.files;
          newItem.id = item.id;
          newItem.tags = item.tags;
          newItem.isDeleted = item.isDeleted;
          newItem.roles = item.roles;
          newItem.canAccess = canAccess;
        });
        return newItem;
      }),
    [headings, roleCanAccessResource, timezone, currentUser, atLeast, addToast]
  );

  const getTableData = useCallback(
    p => {
      setItems([]);
      setShowOptions(false);

      const filter = {showDeleted: showDeleted.current};

      if (query) filter.Search = query;

      const payload = {
        tableId,
        page: p || 1,
        orderBy: orderBy,
        groupBy,
        limit,
        filter: JSON.stringify(filter)
      };

      api.callGet(null, payload).then(({status, data}) => {
        if (status === 200 && data) {
          const formatted = formatTableData(data.notes);

          // Page Limit
          const {total: dataTotal} = data;

          let count = 10;
          const temp = [];
          while (count < dataTotal + 10 && count <= 30) {
            temp.push(count);
            count += 10;
          }
          setLimits(temp);

          setTableData(formatted);
          setTotal(dataTotal);
          setCurrentPage(data.page);
          setPageTotal(data.pages);
        }
      });
    },
    [api, formatTableData, tableId, query, orderBy, groupBy, limit]
  );

  // Format Headings
  useEffect(() => {
    if (isMounted()) {
      const tempHeaders = {};
      columns.map(col => {
        if (fileOnly && col.name === "fileCount") return;
        if (col.name === "fileCount")
          tempHeaders[col.name] = {
            header: <AttachmentColumnHeader />,
            disabled: true
          };
        else if (col.name === "preview")
          tempHeaders[col.name] = {header: col.label, disabled: true};
        else tempHeaders[col.name] = {header: col.label, disabled: false};
      });
      setHeadings(tempHeaders);
    }
  }, [isMounted, api, columns, fileOnly]);

  // Open first table
  useEffect(() => {
    if (isMounted() && index === 1 && !handledOpen?.current) {
      if (handledOpen) handledOpen.current = true;
      setOpen(true);
      if (onOpen) onOpen(true, tableId);
    } else if (isMounted() && onOpen) onOpen(open, tableId);
  }, [isMounted, onOpen, index, tableId, open, handledOpen]);

  useEffect(() => {
    if (isMounted() && triggerOpen !== undefined && triggerOpen !== open) setOpen(triggerOpen);
  }, [isMounted, onOpen, triggerOpen, tableId, open]);

  // Initial Load
  useEffect(() => {
    if (isMounted() && open && !tableData) getTableData();
  }, [isMounted, getTableData, open, tableData]);

  // Update rows on sort or limit
  useEffect(() => {
    if (
      isMounted() &&
      prevOrderBy &&
      prevGroupBy &&
      prevLimit &&
      (prevOrderBy !== orderBy || prevGroupBy !== groupBy || prevLimit !== limit)
    )
      getTableData();
  }, [isMounted, getTableData, prevOrderBy, prevGroupBy, prevLimit, orderBy, groupBy, limit]);

  // Search
  useEffect(() => {
    if (open && (query || query === "")) {
      getTableData();
    }
  }, [open, getTableData, query]);

  const handleSearch = e => {
    setCurrentPage(1);
    setQuery(e.target.value);
  };

  const handleSave = useCallback(
    async target => {
      if (!tags?.length) {
        addToast("This table has no tags. Add at least one tag before uploading rows.", "error");
        return;
      }

      const params = {
        userId: currentUser.publicId,
        tableId,
        facilityId,
        tags: tags.map(t => t.label)
      };

      const fileList = Object.values(target.files);
      const maxBytes = megabytesToBytes(maxFileSize);
      const oversize = [];
      const okSize = [];

      fileList.forEach(file => {
        if (file.size > maxBytes) oversize.push(file);
        else okSize.push(file);
      });

      if (oversize.length > 0) {
        setFailedUploads(oversize);
        setValidUploads(okSize);
        setShowModalFailedUpload(true);
        return;
      }

      try {
        const {uploaded, failed, error} = await uploadFilesInBatch(okSize, {
          userId: currentUser.publicId,
          maxBytes
        });

        if (failed.length > 0) {
          addToast(error || "One or more files could not be uploaded.", "error");
          return;
        }

        const response = await api.callPost(
          {},
          {
            params: {
              ...params,
              fileIds: uploaded.map(({id}) => id).join(",")
            }
          }
        );

        if (response?.status === 201) getTableData();
      } catch (e) {
        addToast(uploadErrorMessage(e), "error");
      }
    },
    [addToast, api, currentUser, facilityId, getTableData, maxFileSize, tableId, tags]
  );

  const fileInputOnChange = useCallback(
    ({target}) => {
      if (target?.files && target.files.length > 0) handleSave(target);
    },
    [handleSave]
  );

  const handleDragStart = useCallback(e => {
    e.preventDefault();
    setShowDropzone(true);
  }, []);

  const handleDragEnd = useCallback(e => {
    e.preventDefault();
    setShowDropzone(false);
  }, []);

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      setShowDropzone(false);

      if (!isDeleted) handleSave(e.dataTransfer);
    },
    [handleSave, isDeleted]
  );

  useEffect(() => {
    const currDropzone = dropzone.current;
    if (isMounted() && open && currDropzone && !isDeleted) {
      currDropzone.addEventListener("dragover", handleDragStart);
      currDropzone.addEventListener("dragleave", handleDragEnd);
      currDropzone.addEventListener("drop", handleDrop);
    }

    if (isMounted() && !open && currDropzone && !isDeleted) {
      currDropzone?.removeEventListener("dragover", handleDragStart);
      currDropzone?.removeEventListener("dragleave", handleDragEnd);
      currDropzone?.removeEventListener("drop", handleDrop);
    }

    return () => {
      currDropzone?.removeEventListener("dragover", handleDragStart);
      currDropzone?.removeEventListener("dragleave", handleDragEnd);
      currDropzone?.removeEventListener("drop", handleDrop);
    };
  }, [isMounted, isDeleted, open, handleDragStart, handleDragEnd, handleDrop]);

  const tableLabel = useMemo(() => {
    let l = "";
    if (inGlobalTables && facilityName) l += `${facilityName.toUpperCase()} ${type} `;
    l += label;
    return l;
  }, [facilityName, inGlobalTables, label, type]);

  const hasTags = tags?.length > 0;

  const addRowTitle = (() => {
    if (!hasTags) return "Add a tag to this table before adding rows";
    if (fileOnly) return "Upload";
    return "Add Row";
  })();

  return (
    <CustomTableWrapper key={name}>
      <TableMenuRow>
        <TableHeading>
          <TableLabel>
            <Abbr title={tableLabel}>{tableLabel}</Abbr>
          </TableLabel>
          {!inGlobalTables && roleCanAccessResource("custom_table", "update") && !isDeleted && (
            <EditButton
              type="button"
              onClick={() => {
                if (handleEditTable) handleEditTable(table);
              }}>
              <Abbr title="Edit Table">
                <FontAwesomeIcon icon={faEdit} />
              </Abbr>
            </EditButton>
          )}
          {inGlobalTables && facilitySlug && roleCanAccessPage("records") && (
            <EditLink to={`/facilities/${facilitySlug}/tables`}>
              <Abbr title="View in Facility">
                <FontAwesomeIcon icon={faUpRightFromSquare} />
              </Abbr>
            </EditLink>
          )}
        </TableHeading>
        <ToggleActive
          type="button"
          onClick={() => {
            setOpen(prev => {
              if (onOpen) onOpen(!prev, tableId);
              return !prev;
            });
            setActive(index);
            setTableData(null);
          }}>
          <Arrow rotate={!open ? "180deg" : "270deg"} />
        </ToggleActive>
      </TableMenuRow>
      {open && (
        <div>
          {!hasTags && !isDeleted && (
            <NoTagsWarning>
              This table has no tags. Add at least one tag
              {roleCanAccessResource("custom_table", "update") && !inGlobalTables
                ? " (Edit Table)"
                : ""}{" "}
              before rows can be added.
            </NoTagsWarning>
          )}
          <TableMenuRow>
            <HeaderMenu>
              <MenuItems active={active}>
                <StyledSearchWrapper>
                  {searchType === "text" && (
                    <SearchIcon>
                      <FontAwesomeIcon icon={faSearch} />
                    </SearchIcon>
                  )}
                  <Search
                    key={searchType}
                    name="search"
                    type={searchType}
                    placeholder="Rows..."
                    onChange={handleSearch}
                  />
                  <Spaced>
                    <ToggleSwitch
                      iconLeft={faKeyboard}
                      iconRight={faCalendarDay}
                      textLeft="Text"
                      textRight="Date"
                      onChange={() => {
                        setSearchType(prev => (prev === "text" ? "date" : "text"));
                        setQuery(null);
                      }}
                      options={["text", "date"]}
                      defaultSelected="text"
                    />
                  </Spaced>
                </StyledSearchWrapper>
                <CustomTableMenu>
                  {(roleCanAccessResource("custom_table_row", "update") ||
                    roleCanAccessResource("custom_table_row", "archive")) && (
                    <>
                      {tableData?.length > 0 && !isDeleted && (
                        <Button
                          type="button"
                          title={showOptions ? "Cancel" : "Options"}
                          onClick={() => setShowOptions(!showOptions)}>
                          {showOptions ? "Cancel" : "Options"}
                        </Button>
                      )}
                      {!isMobileSize() && (
                        <Button
                          type="button"
                          title={showDeleted.current ? "Show Archived" : "Hide Archived"}
                          onClick={() => {
                            showDeleted.current = !showDeleted.current;
                            getTableData();
                          }}>
                          <FontAwesomeIcon icon={showDeleted.current ? faEyeSlash : faEye} />
                          &nbsp;Archived
                        </Button>
                      )}
                    </>
                  )}

                  {roleCanAccessResource("custom_table_row", "export") && tableData?.length > 0 && (
                    <Button type="button" title="Export" onClick={() => setShowModalExport(true)}>
                      <FontAwesomeIcon icon={faFileExport} />
                    </Button>
                  )}
                  {roleCanAccessResource("custom_table_row", "create") && !isDeleted && (
                    <Button
                      type="button"
                      title={addRowTitle}
                      disabled={!hasTags}
                      onClick={() => {
                        if (!hasTags) return;
                        if (!fileOnly) setShowModalAdd(true);
                        else document.getElementById(`single_upload_${tableId}`).click();
                      }}>
                      {fileOnly && (
                        <Input
                          id={`single_upload_${tableId}`}
                          type="file"
                          onChange={fileInputOnChange}
                          multiple
                          hidden
                        />
                      )}
                      <FontAwesomeIcon icon={fileOnly ? faUpload : faFileMedical} />
                    </Button>
                  )}
                </CustomTableMenu>
              </MenuItems>
            </HeaderMenu>
          </TableMenuRow>
          <RowWrapper ref={dropzone}>
            <Dropzone visible={!isDeleted && showDropzone}>
              <div>
                <FontAwesomeIcon icon={faUpload} />
                <Text>Upload Files</Text>
              </div>
            </Dropzone>
            <BaseTable
              headings={headings}
              data={tableData}
              loading={loading}
              orderBy={orderBy}
              setOrderBy={setOrderBy}
              groupBy={groupBy}
              setGroupBy={setGroupBy}
              items={items}
              setItems={setItems}
              showOptions={showOptions}
              setShowOptions={setShowOptions}
              options={
                isDeleted ? null : (
                  <TableEditMenu>
                    {items && items.length === 0 && (
                      <Text inverted>Please select items below for options.</Text>
                    )}
                    {roleCanAccessResource("custom_table_row", "update") &&
                      !fileOnly &&
                      items &&
                      items.length === 1 && (
                        <TableEditMenuButton
                          type="button"
                          onClick={() => {
                            if (items && items.length > 0) {
                              setTargetRow(items[0]);
                              setShowModalAdd(true);
                            }
                          }}>
                          Edit
                        </TableEditMenuButton>
                      )}
                    {roleCanAccessResource("custom_table_row", "update") &&
                      !fileOnly &&
                      items &&
                      items.length >= 1 && (
                        <TableEditMenuButton
                          type="button"
                          onClick={() => setShowModalBulkTags(true)}>
                          Update Tags
                        </TableEditMenuButton>
                      )}
                    {roleCanAccessResource("custom_table_row", "archive") &&
                      items &&
                      items.length >= 1 && (
                        <TableEditMenuButton type="button" onClick={() => setShowModalDelete(true)}>
                          Archive
                        </TableEditMenuButton>
                      )}
                    {items.filter(item => item.isDeleted)?.length > 0 && (
                      <Warning>
                        Warning! One or more of your selected items is already archived.
                      </Warning>
                    )}
                  </TableEditMenu>
                )
              }
            />
          </RowWrapper>
          <TableFooter>
            {total > 0 && (
              <>
                <Inline>
                  {total > 10 && limits.length > 0 && (
                    <PageLimit>
                      <Dropdown
                        options={limits}
                        selection={limit}
                        setSelection={selection => {
                          setLimit(selection);
                          setCurrentPage(1);
                        }}
                      />
                    </PageLimit>
                  )}
                  <Text quiet>
                    {limit <= 30 && pageTotal > 1 && limits.length > 0 && "per page, "}
                    <ModalViewLink
                      disabled={!total || total <= 10}
                      onClick={
                        !handleViewAll || !total || total <= 10
                          ? undefined
                          : () => handleViewAll(table, getTableData)
                      }>
                      {total} total
                    </ModalViewLink>
                  </Text>
                </Inline>
                {limit <= 30 && pageTotal > 1 && (
                  <Pagination
                    current={currentPage}
                    setCurrent={setCurrentPage}
                    pageTotal={pageTotal}
                    updateData={() => getTableData(currentPage)}
                    loading={loading}
                  />
                )}
              </>
            )}
          </TableFooter>
        </div>
      )}

      {showModalAdd && (
        <ModalRowAdd
          visible={showModalAdd}
          setVisible={setShowModalAdd}
          selected={targetRow}
          setSelected={setTargetRow}
          facilityId={facilityId}
          updateTable={getTableData}
          tags={table.tags}
          tableId={tableId}
        />
      )}

      {showModalPreview && targetRow && (
        <ModalRowPreview
          visible={showModalPreview}
          setVisible={setShowModalPreview}
          selected={targetRow}
          setSelected={setTargetRow}
          updateData={getTableData}
          onEdit={
            !fileOnly && roleCanAccessResource("custom_table_row", "update")
              ? selectedRow => {
                  setTargetRow(selectedRow);
                  setShowModalPreview(false);
                  setShowModalAdd(true);
                }
              : undefined
          }
        />
      )}

      {showModalDelete && (
        <ModalRowDelete
          loading={loading}
          visible={showModalDelete}
          setVisible={setShowModalDelete}
          facilityId={facilityId}
          selected={items}
          updateTable={getTableData}
        />
      )}

      {showModalBulkTags && (
        <ModalBulkTagUpdate
          visible={showModalBulkTags}
          setVisible={setShowModalBulkTags}
          facilityId={facilityId}
          selected={items}
          updateTable={() => {
            setItems([]);
            setShowOptions(false);
            getTableData();
          }}
        />
      )}

      {showModalExport && (
        <ModalExport
          visible={showModalExport}
          setVisible={setShowModalExport}
          getExport={async exportParams =>
            api.callGet(null, {
              tableId: tableId,
              ...exportParams
            })
          }
        />
      )}

      {showModalFailedUpload && (
        <ModalFailedUpload
          visible={showModalFailedUpload}
          setVisible={state => {
            setShowModalFailedUpload(state);
            if (!state) {
              setValidUploads(null);
              setFailedUploads(null);
            }
          }}
          facilityId={facilityId}
          updateTable={getTableData}
          tags={table.tags}
          tableId={table.id}
          failed={failedUploads}
          successful={validUploads}
        />
      )}
    </CustomTableWrapper>
  );
};

CustomTable.propTypes = {
  index: PropTypes.number.isRequired,
  table: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    columns: PropTypes.arrayOf(PropTypes.any).isRequired,
    tags: PropTypes.arrayOf(PropTypes.any).isRequired,
    global: PropTypes.bool,
    fileOnly: PropTypes.bool,
    facility: PropTypes.shape({
      name: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      isDeleted: PropTypes.bool,
      type: PropTypes.string.isRequired
    })
  }).isRequired,
  active: PropTypes.number.isRequired,
  setActive: PropTypes.func.isRequired,
  handleEditTable: PropTypes.func,
  handleViewAll: PropTypes.func,
  inGlobalTables: PropTypes.bool,
  onOpen: PropTypes.func,
  triggerOpen: PropTypes.bool,
  handledOpen: PropTypes.objectOf(PropTypes.any)
};

// Style Overrides
const CustomTableWrapper = styled(TableWrapper)`
  min-width: 350px;
`;

const NoTagsWarning = styled(Warning)`
  display: block;
  padding: 0 ${pad}px ${pad}px;
`;

const CustomTableMenu = styled(TableMenu)`
  gap: 7px;

  ${bp(1)} {
    gap: ${pad}px;
  }
`;

const TableMenuRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${pad}px;
`;

const TableLabel = styled(Heading)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TableHeading = styled.div`
  display: inline-flex;
  overflow: hidden;
  max-width: calc(100% - 30px);
`;

const EditButton = styled.button`
  ${voice.normal};
  margin-left: ${pad}px;
  height: min-height;
  align-self: center;
  width: max-content;

  svg {
    fill: ${({theme}) => theme.secondary};
  }
`;

const EditLink = styled(NavLink)`
  ${voice.normal};
  margin-left: ${pad}px;
  height: min-height;
  align-self: center;

  svg {
    fill: ${({theme}) => theme.secondary};
  }
`;

const HeaderMenu = styled(Inline)`
  gap: ${pad}px;
  display: block;
  width: 100%;

  ${bp(2)} {
    display: flex;
  }
`;

const MenuItems = styled(Inline)`
  justify-content: space-between;
  width: 100%;
  opacity: ${({active}) => active};
`;

const ToggleActive = styled(Button)`
  background: none;
`;

const Spaced = styled.div`
  margin-left: ${pad / 2}px;

  ${bp(1)} {
    margin-left: ${pad}px;
  }
`;

const StyledSearchWrapper = styled(SearchWrapper)`
  max-width: calc(100% - 176px);
`;

const PageLimit = styled.div`
  margin-right: ${pad}px;

  select {
    padding: ${pad / 2}px;
  }
`;

const RowWrapper = styled.div`
  position: relative;
`;

const Dropzone = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({theme}) => hexToRgba(theme.primary, 0.1)};
  z-index: ${z("above")};

  opacity: 0;
  visibility: hidden;
  transition: ${transition};

  ${({visible}) =>
    visible &&
    css`
      opacity: 1;
      visibility: visible;
    `};

  div {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  ${Text} {
    color: ${({theme}) => theme.primary};
    text-align: center;
    ${voice.strong};
  }

  svg {
    fill: ${({theme}) => theme.primary};
    margin: 0 auto;
    ${voice.strong};
  }
`;

const NotAllowed = styled(Button)`
  cursor: not-allowed;
`;

const ModalViewLink = styled.span`
  ${({disabled}) =>
    !disabled &&
    css`
      text-decoration: underline;
      cursor: pointer;
    `}
`;

const Expiration = styled.div`
  ${({expired, theme}) =>
    expired &&
    css`
      color: ${theme.error};
    `}
`;

export default CustomTable;
